import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresRole?: 'admin' | 'approver';
    title?: string;
    /** pagina toegankelijk voor pending-gebruikers */
    allowPending?: boolean;
    layout?: 'blank' | 'app';
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { layout: 'blank', title: 'Inloggen' },
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/AuthCallbackView.vue'),
    meta: { layout: 'blank', title: 'Bezig met inloggen' },
  },
  {
    path: '/pending',
    name: 'pending',
    component: () => import('@/views/PendingView.vue'),
    meta: { requiresAuth: true, allowPending: true, title: 'Wachten op goedkeuring' },
  },
  { path: '/', name: 'home', redirect: '/products' },
  {
    path: '/products',
    name: 'products',
    component: () => import('@/views/ProductListView.vue'),
    meta: { requiresAuth: true, title: 'Producten' },
  },
  {
    path: '/products/new',
    name: 'product-new',
    component: () => import('@/views/ProductNewView.vue'),
    meta: { requiresAuth: true, title: 'Nieuw product' },
  },
  {
    path: '/products/:id(\\d+)',
    name: 'product-detail',
    component: () => import('@/views/ProductDetailView.vue'),
    meta: { requiresAuth: true, title: 'Product' },
  },
  {
    path: '/products/:id(\\d+)/lines/:lineId(\\d+)',
    name: 'line-detail',
    component: () => import('@/views/LineEditorView.vue'),
    meta: { requiresAuth: true, title: 'Productregel' },
  },
  {
    path: '/import',
    name: 'import',
    component: () => import('@/views/ImportView.vue'),
    meta: { requiresAuth: true, title: 'Importeren' },
  },
  {
    path: '/products/:id(\\d+)/import',
    name: 'product-import',
    component: () => import('@/views/ImportView.vue'),
    meta: { requiresAuth: true, title: 'Importeren' },
  },
  {
    path: '/unit4/load',
    name: 'unit4-load',
    component: () => import('@/views/Unit4LoadView.vue'),
    meta: { requiresAuth: true, title: 'Laden uit Unit4' },
  },
  {
    path: '/feedback',
    name: 'feedback',
    component: () => import('@/views/FeedbackView.vue'),
    meta: { requiresAuth: true, title: 'Feedback' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/UsersView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', title: 'Gebruikers' },
  },
  {
    path: '/admin/templates',
    name: 'admin-templates',
    component: () => import('@/views/admin/TemplatesView.vue'),
    meta: { requiresAuth: true, requiresRole: 'approver', title: 'Sjablonen' },
  },
  {
    path: '/admin/product-codes',
    name: 'admin-product-codes',
    component: () => import('@/views/admin/ProductCodesView.vue'),
    meta: { requiresAuth: true, requiresRole: 'approver', title: 'Productcodes' },
  },
  {
    path: '/admin/sizing',
    name: 'admin-sizing',
    component: () => import('@/views/admin/SizingReferenceView.vue'),
    meta: { requiresAuth: true, requiresRole: 'approver', title: 'Actuator-sizing' },
  },
  {
    path: '/admin/settings',
    name: 'admin-settings',
    component: () => import('@/views/admin/SettingsView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', title: 'Instellingen' },
  },
  {
    path: '/admin/sync-log',
    name: 'admin-sync-log',
    component: () => import('@/views/admin/SyncLogView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', title: 'Sync-historie' },
  },
  {
    path: '/admin/lookup-jobs',
    name: 'admin-lookup-jobs',
    component: () => import('@/views/admin/LookupJobsView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', title: 'AI-zoekopdrachten' },
  },
  {
    path: '/review',
    name: 'review',
    component: () => import('@/views/ReviewView.vue'),
    meta: { requiresAuth: true, requiresRole: 'approver', title: 'Beoordelen' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Niet gevonden' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.initialized) await auth.initialize();

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (auth.isAuthenticated && (to.name === 'login' || to.name === 'auth-callback') && !to.query.token) {
    return { name: 'products' };
  }
  if (auth.isAuthenticated && !auth.isActive && !to.meta.allowPending && to.name !== 'login') {
    return { name: 'pending' };
  }
  if (auth.isAuthenticated && auth.isActive && to.name === 'pending') {
    return { name: 'products' };
  }
  if (to.meta.requiresRole === 'admin' && !auth.isAdmin) {
    return { name: 'products' };
  }
  if (to.meta.requiresRole === 'approver' && !auth.isApprover) {
    return { name: 'products' };
  }
  return true;
});

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · Klingerator` : 'Klingerator';
});
